import { COLOR_BY_ID, colorName, type ColorId } from '../shared/colors';
import {
  CHANNEL_HOP_AT,
  FINAL_BREATHER_MS,
  PROTOCOLS,
  PROTOCOL_BREATHER_MS,
  STAGING_MS,
  SUBJECT_STABILITY,
} from '../shared/protocols';
import type { PlayerPublic, Profile, ProtocolId, Question, SeatId } from '../shared/types';
import type { MatchEndPayload, RoundResultPayload } from '../shared/messages';
import type { AudioEngine } from './audio';
import type { SoloRecord } from './storage';
import type { LoreCollectResult, LorePick, LoreRepository } from './lore/repository';
import { LORE_SECTIONS } from './lore/types';
import { endStory } from './lore/story';
import { hashString, mulberry32, randomInt, shuffle, type Rng } from '../shared/rng';

const HORROR_IMAGES = [
  '/images/lore/horror-face.jpg',
  '/images/lore/site06-corridor.jpg',
  '/images/lore/civil-poster.jpg',
  '/images/lore/newspaper.jpg',
  '/images/lore/protocol-card.jpg',
];

for (const src of HORROR_IMAGES) {
  const image = new Image();
  image.src = src;
  void image.decode?.().catch(() => {});
}

const FIXED_CORNER_FONT_INDEX: Record<number, number> = {
  0: 0,
  2: 5,
  6: 3,
  8: 2,
};

const ACTIVE_CELLS: Record<ProtocolId, number[]> = {
  1: [0, 2, 6, 8],
  2: [0, 2, 4, 6, 8],
  3: [0, 1, 2, 4, 6, 8],
  4: [0, 1, 2, 4, 6, 7, 8],
  5: [0, 1, 2, 3, 5, 6, 7, 8],
  6: [0, 1, 2, 3, 5, 6, 7, 8],
};

const FIXED_CORNER_LABELS: Record<number, ColorId> = {
  0: 'blue',
  2: 'red',
  6: 'yellow',
  8: 'green',
};

const KEY_FONT_COLORS: { hex: string; light: boolean }[] = [
  { hex: '#ff3b30', light: false },
  { hex: '#ff8a00', light: false },
  { hex: '#ffd60a', light: true },
  { hex: '#2ee66b', light: true },
  { hex: '#21d4d8', light: true },
  { hex: '#3b7bff', light: false },
  { hex: '#c04bff', light: false },
  { hex: '#ff4fa3', light: false },
];

const BULB_COUNT = SUBJECT_STABILITY;

type Phase = 'waiting' | 'countdown' | 'staging' | 'playing' | 'ended' | 'reconnecting';

type GameMode = 'solo' | 'online';

export interface GameScreenOptions {
  mode: GameMode;
  selfUid: string;
  selfName: string;
  audio: AudioEngine;
  lore: LoreRepository;
  onAnswer: (color: ColorId) => void;
  onExit: () => void;
  onRematch?: () => void;
  onReturnLobby?: () => void;
  onSoloRetry?: () => void;
  onOpenArchive?: () => void;
}

interface QuestionView {
  question: Question;
  displayAtLocal: number;
  deadlineLocal: number;
  pollution: number;
  stabilities: Record<string, number>;
}

const ROMAN: Record<ProtocolId, string> = {
  1: 'I',
  2: 'II',
  3: 'III',
  4: 'IV',
  5: 'V',
  6: 'VI',
};

function esc(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function reaction(value: number | null | undefined): string {
  return typeof value === 'number' ? `${value}ms` : '—';
}

function formatDelta(value: number | undefined): string {
  if (typeof value !== 'number' || value === 0) return '±0';
  return value > 0 ? `+${value}` : `${value}`;
}

function sectionLabel(id: LoreCollectResult['doc']['section']): string {
  return LORE_SECTIONS.find((section) => section.id === id)?.nameZh ?? '未分类';
}

function bulbRow(): string {
  return Array.from(
    { length: BULB_COUNT },
    (_, index) => `<i class="bulb" data-bulb="${index}"><em></em></i>`,
  ).join('');
}

function screenRow(): string {
  return Array.from(
    { length: 4 },
    (_, index) => `
      <div class="crt-mount" data-mount="${index}">
        <div class="crt" data-screen="${index}" data-state="idle">
          <i class="crt-flag" data-screen-tag></i>
          <div class="crt-body">
            <div class="crt-dial" aria-hidden="true"></div>
            <div class="crt-image" data-screen-image></div>
            <div class="crt-word" data-screen-word></div>
            <div class="crt-scan" aria-hidden="true"></div>
            <div class="crt-noise" aria-hidden="true"></div>
          </div>
        </div>
      </div>`,
  ).join('');
}

interface GameRefs {
  screenWall: HTMLElement;
  mirrorPanel: HTMLElement;
  mirrorRival: HTMLElement;
  projector: HTMLElement;
  projectorTag: HTMLElement;
  projectorCount: HTMLElement;
  projectorSub: HTMLElement;
  screens: HTMLElement[];
  screenWords: HTMLElement[];
  screenImages: HTMLElement[];
  screenTags: HTMLElement[];
  keypadLed: HTMLElement;
  ping: HTMLElement;
  cells: HTMLButtonElement[];
  protocolRoman: HTMLElement;
  selfLamps: HTMLElement;
  rivalLamps: HTMLElement;
  captionText: HTMLElement;
  waitingOverlay: HTMLElement;
  waitingCode: HTMLElement;
  waitingList: HTMLElement;
  waitingHint: HTMLElement;
  resultOverlay: HTMLElement;
  resultCard: HTMLElement;
  resultTitle: HTMLElement;
  resultColor: HTMLElement;
  resultColorName: HTMLElement;
  endOverlay: HTMLElement;
  endStamp: HTMLElement;
  endTitle: HTMLElement;
  endSub: HTMLElement;
  endScore: HTMLElement;
  endRating: HTMLElement;
  endStats: HTMLElement;
  endActions: HTMLElement;
  endRematchStatus: HTMLElement;
  confirmOverlay: HTMLElement;
  loreDrop: HTMLElement;
  loreDropLife: HTMLElement;
  endLore: HTMLElement;
  endStory: HTMLElement;
  scareFlash: HTMLElement;
  collapseBlack: HTMLElement;
}

export class GameScreen {
  private readonly root: HTMLElement;
  private readonly opts: GameScreenOptions;
  private phase: Phase = 'waiting';
  private selfUid: string;
  private rivalUid: string | null = null;
  private currentQuestion: Question | null = null;
  private currentQid: string | null = null;
  private displayAtLocal = 0;
  private deadlineLocal = 0;
  private answerEnabled = false;
  private answered = false;
  private selectedColor: ColorId | null = null;
  private selectedCell: number | null = null;
  private liveScreens: number[] = [];
  private hopTo: number | null = null;
  private hopDone = false;
  private displayTimer: number | null = null;
  private deadlineTimer: number | null = null;
  private resultTimer: number | null = null;
  private countdownTimer: number | null = null;
  private projectorTimer: number | null = null;
  private captionTimer: number | null = null;
  private scareTimer: number | null = null;
  private collapseTimer: number | null = null;
  private rafId: number | null = null;
  private lastDialAt = 0;
  private lastCountdownValue = -1;
  private lastQuestionGeneration = 0;
  private stagingTimers: number[] = [];
  private pollution = 0;
  private currentProtocol: ProtocolId = 1;
  private selfStability = SUBJECT_STABILITY;
  private rivalStability = SUBJECT_STABILITY;
  private roomCode = '';
  private ending = false;
  private maxProtocol: ProtocolId = 1;
  private activeDrop: LorePick | null = null;
  private dropTimer: number | null = null;
  private dropIds = new Set<string>();
  private loreResults: LoreCollectResult[] = [];
  private loreLeakPlayed = false;
  private loreLeakTimer: number | null = null;

  private readonly refs: GameRefs;

  constructor(root: HTMLElement, options: GameScreenOptions) {
    this.root = root;
    this.opts = options;
    this.selfUid = options.selfUid;
    this.render();
    this.refs = this.collectRefs();
    this.bindEvents();
    this.setPollution(0);
  }

  private render(): void {
    const mode = this.opts.mode;
    this.root.innerHTML = `
      <section class="game" data-mode="${mode}" data-phase="waiting" data-pollution-tier="calm"
               data-self-seat="1" data-rival-seat="2">
        <div class="room">
          <div class="room-light" aria-hidden="true"></div>
          <div class="room-grain" aria-hidden="true"></div>

          <span class="wall-plate" aria-hidden="true"><b data-protocol-roman>I</b></span>

          <div class="screen-wall" data-screen-wall data-effect="0" aria-live="off">
            ${screenRow()}
            <div class="mirror-panel" data-mirror>
              <div class="mirror-glass">
                <div class="mirror-shadows" aria-hidden="true"><i></i><i></i><i></i></div>
                <div class="mirror-rival" data-mirror-rival aria-hidden="true">
                  <div class="mirror-face" data-mirror-face></div>
                  <div class="mirror-backs" aria-hidden="true">
                    <span class="mirror-screens"></span>
                    <span class="mirror-keypad"></span>
                  </div>
                </div>
                <div class="mirror-glare" aria-hidden="true"></div>
                <div class="projector" data-projector aria-hidden="true">
                  <b data-projector-count>3</b>
                  <strong data-projector-tag>协议一</strong>
                  <small data-projector-sub>受试环境正在准备</small>
                </div>
                <div class="lamp-rig" data-lamp-rig aria-hidden="true">
                  <div class="lamp-bank is-rival" data-lamp-bank="rival">${bulbRow()}</div>
                  <span class="lamp-bridge"></span>
                  <div class="lamp-bank is-self" data-lamp-bank="self">${bulbRow()}</div>
                </div>
              </div>
            </div>
          </div>

          <p class="stage-caption"><span data-caption-text>NO SIGNAL</span></p>
        </div>

        <footer class="answer-deck">
          <i class="prop prop-crank" aria-hidden="true"><b></b><em></em></i>
          <i class="prop prop-bag" aria-hidden="true"><b></b><em></em></i>
          <div class="keypad-bezel">
            <i class="keypad-screw is-tl"></i><i class="keypad-screw is-tr"></i>
            <i class="keypad-screw is-bl"></i><i class="keypad-screw is-br"></i>
            <div class="keypad-head">
              <b data-keypad-led>STANDBY</b>
              <span class="ping-readout" data-ping>-- MS</span>
            </div>
            <div class="color-grid" data-options role="group" aria-label="按按键上的颜色词选择">
              ${Array.from(
                { length: 9 },
                (_, index) => `
                <button class="color-cell is-dead" type="button" data-cell="${index}" disabled>
                  <span class="cell-label"></span>
                  <span class="cell-key">${index + 1}</span>
                </button>`,
              ).join('')}
            </div>
            <span class="keypad-foot">读屏幕上那行字的颜色 · 按写着这个颜色的键</span>
          </div>
          <button class="lore-drop" type="button" data-action="collect-drop" title="回收档案碎片" hidden>
            <span class="lore-drop-mark">?</span>
            <span class="lore-drop-life"><i data-drop-life></i></span>
          </button>
          <button class="deck-abort" type="button" data-action="abort">终止</button>
        </footer>

        <button class="abort-button" type="button" data-action="abort">EXIT <small>ESC</small></button>

        <div class="overlay waiting-overlay" data-overlay="waiting">
          <div class="wait-card">
            <div class="seal">LINK / 06</div>
            <p class="overlay-kicker">DUAL SUBJECT PROTOCOL</p>
            <h2>等待受试者</h2>
            <p class="overlay-sub">Send this room code to establish the link.</p>
            <button class="room-code" type="button" data-copy-code>
              <span data-waiting-code>------</span>
              <small>CLICK TO COPY</small>
            </button>
            <div class="waiting-list" data-waiting-list></div>
            <p class="waiting-hint" data-waiting-hint>保持连接，匹配建立后将自动开始校准。</p>
            <button class="btn btn-ghost btn-small" type="button" data-action="abort">断开连接 / DISCONNECT</button>
          </div>
        </div>

        <div class="overlay result-overlay" data-overlay="result" aria-live="assertive">
          <div class="result-card" data-result-card data-tone="neutral">
            <strong class="result-title" data-result-title>—</strong>
            <span class="result-color-chip" data-result-color></span>
            <span data-result-color-name>—</span>
          </div>
        </div>

        <div class="overlay end-overlay" data-overlay="end">
          <div class="end-card">
            <div class="end-stamp" data-end-stamp>SESSION CLOSED</div>
            <p class="overlay-kicker">FINAL CALIBRATION REPORT</p>
            <h2 data-end-title>—</h2>
            <p class="end-sub" data-end-sub>—</p>
            <div class="end-scoreboard">
              <div class="end-score-main" data-end-score>0 — 0</div>
              <div class="end-rating" data-end-rating>±0</div>
            </div>
            <div class="end-stats" data-end-stats></div>
            <p class="end-story" data-end-story></p>
            <div class="end-lore" data-end-lore></div>
            <p class="end-rematch-status" data-end-rematch-status></p>
            <div class="end-actions" data-end-actions></div>
          </div>
        </div>

        <div class="overlay confirm-overlay" data-overlay="confirm">
          <div class="confirm-card">
            <span class="seal is-red">WARNING</span>
            <h2>终止当前链路？</h2>
            <p>正在进行的对局会被记录为失败。污染数据不会保留。</p>
            <div class="confirm-actions">
              <button class="btn btn-danger" type="button" data-confirm="yes">终止 / ABORT</button>
              <button class="btn btn-ghost" type="button" data-confirm="no">继续 / RESUME</button>
            </div>
          </div>
        </div>

        <div class="scare-flash" data-scare-flash aria-hidden="true"></div>
        <div class="collapse-black" data-collapse-black aria-hidden="true"></div>
        <div class="toast" data-toast role="status"></div>
      </section>
    `;
  }

  private collectRefs(): GameRefs {
    const q = <T extends HTMLElement>(selector: string): T => {
      const node = this.root.querySelector<T>(selector);
      if (!node) throw new Error(`Missing game screen element: ${selector}`);
      return node;
    };
    const screens = [...this.root.querySelectorAll<HTMLElement>('[data-screen]')];
    return {
      screenWall: q('[data-screen-wall]'),
      mirrorPanel: q('[data-mirror]'),
      mirrorRival: q('[data-mirror-rival]'),
      projector: q('[data-projector]'),
      projectorTag: q('[data-projector-tag]'),
      projectorCount: q('[data-projector-count]'),
      projectorSub: q('[data-projector-sub]'),
      screens,
      screenWords: screens.map((screen) => screen.querySelector<HTMLElement>('[data-screen-word]')!),
      screenImages: screens.map((screen) => screen.querySelector<HTMLElement>('[data-screen-image]')!),
      screenTags: screens.map((screen) => screen.querySelector<HTMLElement>('[data-screen-tag]')!),
      keypadLed: q('[data-keypad-led]'),
      ping: q('[data-ping]'),
      cells: [...this.root.querySelectorAll<HTMLButtonElement>('[data-cell]')],
      protocolRoman: q('[data-protocol-roman]'),
      selfLamps: q('[data-lamp-bank="self"]'),
      rivalLamps: q('[data-lamp-bank="rival"]'),
      captionText: q('[data-caption-text]'),
      waitingOverlay: q('[data-overlay="waiting"]'),
      waitingCode: q('[data-waiting-code]'),
      waitingList: q('[data-waiting-list]'),
      waitingHint: q('[data-waiting-hint]'),
      resultOverlay: q('[data-overlay="result"]'),
      resultCard: q('[data-result-card]'),
      resultTitle: q('[data-result-title]'),
      resultColor: q('[data-result-color]'),
      resultColorName: q('[data-result-color-name]'),
      endOverlay: q('[data-overlay="end"]'),
      endStamp: q('[data-end-stamp]'),
      endTitle: q('[data-end-title]'),
      endSub: q('[data-end-sub]'),
      endScore: q('[data-end-score]'),
      endRating: q('[data-end-rating]'),
      endStats: q('[data-end-stats]'),
      endActions: q('[data-end-actions]'),
      endRematchStatus: q('[data-end-rematch-status]'),
      confirmOverlay: q('[data-overlay="confirm"]'),
      loreDrop: q('[data-action="collect-drop"]'),
      loreDropLife: q('[data-drop-life]'),
      endLore: q('[data-end-lore]'),
      endStory: q('[data-end-story]'),
      scareFlash: q('[data-scare-flash]'),
      collapseBlack: q('[data-collapse-black]'),
    };
  }

  private bindEvents(): void {
    this.root.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeyDown);
  }

  private onClick = (event: Event): void => {
    const target = event.target as HTMLElement;
    const cell = target.closest<HTMLButtonElement>('[data-cell]');
    if (cell && cell.dataset.color) {
      this.submitAnswer(cell.dataset.color as ColorId, Number(cell.dataset.cell));
      return;
    }
    const copy = target.closest('[data-copy-code]');
    if (copy) {
      if (this.roomCode) {
        void navigator.clipboard
          ?.writeText(this.roomCode)
          .then(() => this.showToast('房间码已复制 // CODE COPIED'))
          .catch(() => this.showToast(this.roomCode));
      }
      return;
    }
    const confirm = target.closest<HTMLElement>('[data-confirm]');
    if (confirm) {
      this.refs.confirmOverlay.classList.remove('is-active');
      if (confirm.dataset.confirm === 'yes') this.opts.onExit();
      return;
    }
    const action = target.closest<HTMLElement>('[data-action]')?.dataset.action;
    if (action === 'collect-drop') {
      this.collectActiveDrop(false);
    } else if (action === 'archive') {
      this.opts.onOpenArchive?.();
    } else if (action === 'abort') {
      if (this.phase === 'ended') {
        this.opts.onReturnLobby?.();
      } else {
        this.refs.confirmOverlay.classList.add('is-active');
      }
    } else if (action === 'lobby') {
      this.opts.onReturnLobby?.();
    } else if (action === 'rematch') {
      this.opts.onRematch?.();
    } else if (action === 'retry') {
      this.opts.onSoloRetry?.();
    }
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    if (event.key === 'Escape') {
      if (this.refs.confirmOverlay.classList.contains('is-active')) {
        this.refs.confirmOverlay.classList.remove('is-active');
        return;
      }
      if (this.phase !== 'ended') {
        event.preventDefault();
        this.refs.confirmOverlay.classList.add('is-active');
      }
      return;
    }
    if (!this.answerEnabled || this.answered || this.phase !== 'playing') return;
    const number = Number(event.key);
    if (!Number.isInteger(number) || number < 1 || number > 9) return;
    const cell = this.refs.cells[number - 1];
    if (cell?.dataset.color) {
      event.preventDefault();
      this.submitAnswer(cell.dataset.color as ColorId, number - 1);
    }
  };

  private submitAnswer(color: ColorId, cellIndex: number): void {
    if (!this.answerEnabled || this.answered || this.phase !== 'playing') return;
    this.answered = true;
    this.selectedColor = color;
    this.selectedCell = cellIndex;
    this.answerEnabled = false;
    this.opts.audio.playClick();
    for (const button of this.refs.cells) {
      button.disabled = true;
      if (button.dataset.cell === String(cellIndex)) button.classList.add('is-selected');
    }
    this.opts.onAnswer(color);
  }

  setPlayers(players: PlayerPublic[], selfUid = this.selfUid): void {
    this.selfUid = selfUid;
    const self = players.find((player) => player.uid === selfUid);
    const rival = players.find((player) => player.uid !== selfUid);
    this.rivalUid = rival?.uid ?? null;
    const game = this.root.querySelector<HTMLElement>('.game');
    if (game) {
      game.dataset.selfSeat = String(self?.seat ?? 1);
      game.dataset.rivalSeat = String(rival?.seat ?? 2);
    }
    const stabilities: Record<string, number> = {};
    for (const player of players) stabilities[player.uid] = player.stability;
    if (Object.keys(stabilities).length) this.setStabilities(stabilities);
    this.setOpponentConnection(rival?.connected ?? false);
    this.refreshWaitingList(players, selfUid);
  }

  private refreshWaitingList(players: PlayerPublic[], selfUid: string): void {
    if (!players.length) {
      this.refs.waitingList.innerHTML = `<div class="subject-row is-empty"><i></i><span>AWAITING SUBJECT</span><small>--</small></div>`;
      return;
    }
    this.refs.waitingList.innerHTML = players
      .map(
        (player) => `
          <div class="subject-row ${player.connected ? 'is-online' : 'is-offline'}" data-seat="${player.seat}">
            <i></i>
            <span>${esc(player.name)}${player.uid === selfUid ? ' <em>YOU</em>' : ''}</span>
            <small>${player.seat === 1 ? '1P' : '2P'}</small>
          </div>`,
      )
      .join('');
    if (players.length < 2) {
      this.refs.waitingList.insertAdjacentHTML(
        'beforeend',
        `<div class="subject-row is-empty"><i></i><span>AWAITING SUBJECT</span><small>--</small></div>`,
      );
    }
  }

  showWaiting(code: string, players: PlayerPublic[], selfUid = this.selfUid): void {
    this.phase = 'waiting';
    this.setRootPhase();
    this.resetCollapseState();
    this.cancelStaging();
    this.resetLoreRun();
    this.roomCode = code;
    this.opts.audio.stopHorror();
    this.resetScreens();
    this.resetGrid();
    this.clearProjector();
    this.setDial(0);
    this.refs.waitingCode.textContent = code || '------';
    this.setPlayers(players, selfUid);
    this.refs.waitingOverlay.classList.add('is-active');
    this.refs.resultOverlay.classList.remove('is-active');
    this.refs.endOverlay.classList.remove('is-active');
    this.root.querySelector('.game')?.classList.remove('is-sudden-death');
    this.setPollution(0);
    this.setProtocol(1, false);
    this.clearCountdown();
  }

  /** 倒计时投影在单面镜上，结束后进入入场编排 */
  showCountdown(startAtLocal: number, durationMs: number, selfUid = this.selfUid): void {
    this.resetCollapseState();
    this.cancelStaging();
    if (this.ending) {
      this.resetLoreRun();
      this.ending = false;
    }
    this.phase = 'countdown';
    this.selfUid = selfUid;
    this.setRootPhase();
    this.resetScreens();
    this.resetGrid();
    this.setDial(0);
    this.opts.audio.startHorror();
    this.opts.audio.setHorrorIntensity(24);
    this.root.querySelector('.game')?.classList.remove('is-sudden-death');
    this.refs.waitingOverlay.classList.remove('is-active');
    this.refs.resultOverlay.classList.remove('is-active');
    this.refs.endOverlay.classList.remove('is-active');
    this.refs.projector.dataset.state = 'countdown';
    this.refs.projector.classList.add('is-active');
    this.refs.projectorSub.textContent = '受试环境正在准备';
    this.clearCountdown();
    this.lastCountdownValue = -1;
    const start = startAtLocal;
    const total = Math.max(1, durationMs);
    const tick = () => {
      const remain = start - Date.now();
      const value = Math.max(0, Math.ceil(remain / 1000));
      if (value !== this.lastCountdownValue) {
        this.lastCountdownValue = value;
        this.refs.projectorCount.textContent = String(value || 0);
        if (value > 0) this.opts.audio.playTick(value === 1);
      }
      if (remain <= 0) {
        this.clearCountdown();
        this.refs.projector.classList.remove('is-active');
        this.runStaging();
        return;
      }
      const progress = 1 - Math.max(0, remain) / total;
      this.refs.projectorCount.style.setProperty('--count-progress', String(progress));
      this.countdownTimer = window.setTimeout(tick, 80);
    };
    tick();
  }

  /**
   * 入场编排：大灯压下 → 摇把与文档袋沉入黑暗 → 屏幕点亮两次 →
   * 键盘就绪 → 单向镜转透露出对面受试者 → 镜子中间的容量灯亮起。
   */
  private runStaging(): void {
    this.cancelStaging();
    this.phase = 'staging';
    this.setRootPhase();
    const game = this.root.querySelector<HTMLElement>('.game');
    const beat = (delay: number, fn: () => void): void => {
      const timer = window.setTimeout(() => {
        this.stagingTimers = this.stagingTimers.filter((entry) => entry !== timer);
        fn();
      }, delay);
      this.stagingTimers.push(timer);
    };

    game?.classList.remove('is-booting-screens');
    this.opts.audio.playLampPress();
    game?.classList.add('is-lights-down');
    game?.classList.add('is-props-dark');

    beat(900, () => {
      this.opts.audio.playScreenBoot();
      game?.classList.add('is-booting-screens');
    });

    beat(1750, () => {
      game?.classList.remove('is-booting-screens');
      this.opts.audio.playKeypadReady();
      game?.classList.add('is-keypad-ready');
      this.refs.keypadLed.textContent = 'READY';
    });

    if (this.opts.mode === 'online') {
      beat(2300, () => {
        this.opts.audio.playGlassReveal();
        game?.classList.add('is-glass-open');
      });
    }

    beat(2450, () => {
      const config = PROTOCOLS[this.currentProtocol];
      this.showProjector(config.tagZh, `${config.nameZh} / ${config.nameEn}`, 1050);
    });

    beat(2950, () => {
      this.opts.audio.playBulbOn();
      game?.classList.add('is-lamps-live');
    });

    beat(STAGING_MS, () => {
      this.stagingTimers = [];
      this.phase = 'playing';
      this.setRootPhase();
      this.refs.keypadLed.textContent = 'ARMED';
    });
  }

  private cancelStaging(): void {
    for (const timer of this.stagingTimers) window.clearTimeout(timer);
    this.stagingTimers = [];
    const game = this.root.querySelector<HTMLElement>('.game');
    game?.classList.remove(
      'is-lights-down',
      'is-props-dark',
      'is-booting-screens',
      'is-keypad-ready',
      'is-glass-open',
      'is-lamps-live',
    );
  }

  setProtocol(protocolId: ProtocolId, announce = false): void {
    const config = PROTOCOLS[protocolId];
    const advanced = protocolId > this.maxProtocol;
    this.currentProtocol = protocolId;
    this.maxProtocol = Math.max(this.maxProtocol, protocolId) as ProtocolId;
    this.refs.protocolRoman.textContent = ROMAN[protocolId];
    this.refs.screenWall.dataset.effect = String(config.effectLevel);
    this.opts.audio.setHorrorIntensity(18 + protocolId * 7 + this.pollution * 0.35);
    if (announce) this.showProjector(config.tagZh, `${config.nameZh} / ${config.nameEn}`);
    if (advanced && this.phase === 'playing') {
      this.maybeSpawnDrop(protocolId);
      if (protocolId === 4) {
        this.opts.audio.playScream(protocolId / 6);
        this.triggerLoreLeak(1200);
      }
      if (protocolId === 6) this.triggerLoreLeak(1800);
    }
  }

  /** 白色粗体投影在单面镜上，配开灯音效；消失时关灯 */
  private showProjector(tagZh: string, sub: string, durationMs?: number): void {
    const hold = durationMs ?? (this.currentProtocol === 6 ? FINAL_BREATHER_MS : PROTOCOL_BREATHER_MS);
    if (this.projectorTimer !== null) window.clearTimeout(this.projectorTimer);
    this.refs.projector.dataset.state = 'tag';
    this.refs.projectorTag.textContent = tagZh;
    this.refs.projectorSub.textContent = sub;
    this.refs.projector.classList.add('is-active');
    this.opts.audio.playLampOn();
    this.opts.audio.playProtocol();
    this.projectorTimer = window.setTimeout(() => {
      this.projectorTimer = null;
      this.refs.projector.classList.remove('is-active');
      this.opts.audio.playLampPress();
    }, hold - 260);
  }

  private clearProjector(): void {
    if (this.projectorTimer !== null) {
      window.clearTimeout(this.projectorTimer);
      this.projectorTimer = null;
    }
    this.refs.projector.classList.remove('is-active');
  }

  /** 血量：镜子里的灯管，自己右侧、对面左侧，1P 蓝 2P 红 */
  setStabilities(stabilities: Record<string, number> | undefined): void {
    if (!stabilities) return;
    const selfValue = stabilities[this.selfUid];
    if (typeof selfValue === 'number') this.applyLamps(this.refs.selfLamps, selfValue, this.selfStability);
    const rivalValue = this.rivalUid ? stabilities[this.rivalUid] : undefined;
    if (typeof rivalValue === 'number') this.applyLamps(this.refs.rivalLamps, rivalValue, this.rivalStability);
  }

  private applyLamps(bank: HTMLElement, value: number, previous: number): void {
    const next = Math.max(0, Math.min(BULB_COUNT, value));
    const isSelf = bank.dataset.lampBank === 'self';
    if (isSelf) this.selfStability = next;
    else this.rivalStability = next;
    bank.querySelectorAll<HTMLElement>('[data-bulb]').forEach((bulb, index) => {
      const lit = index < next;
      bulb.classList.toggle('is-lit', lit);
      bulb.classList.toggle('is-burnt', !lit);
      if (!lit && index === next && previous > next) {
        bulb.classList.remove('is-blowing');
        void bulb.offsetWidth;
        bulb.classList.add('is-blowing');
      }
    });
    if (previous > next && this.phase !== 'ended') {
      this.opts.audio.playBulbOut();
      this.triggerGlitch();
    }
  }

  setPollution(level: number): void {
    this.pollution = Math.max(0, Math.min(100, Math.round(level)));
    const tier =
      this.pollution >= 90
        ? 'overload'
        : this.pollution >= 70
          ? 'critical'
          : this.pollution >= 40
            ? 'elevated'
            : 'calm';
    const game = this.root.querySelector<HTMLElement>('.game');
    if (game) game.dataset.pollutionTier = tier;
    this.opts.audio.setPollution(this.pollution);
    this.opts.audio.setHorrorIntensity(18 + this.currentProtocol * 7 + this.pollution * 0.35);
    if (this.pollution >= 70 && !this.loreLeakPlayed && this.phase === 'playing') {
      this.triggerLoreLeak(0);
    }
  }

  private triggerLoreLeak(delay: number): void {
    if (this.loreLeakPlayed) return;
    this.loreLeakPlayed = true;
    const fire = () => {
      this.loreLeakTimer = null;
      if (this.phase !== 'playing') return;
      this.opts.audio.playLoreLeak('/audio/lore/m06-leak-zh.wav');
      this.setCaption('RECOVERED AUDIO // M-06');
    };
    if (delay > 0) {
      this.loreLeakTimer = window.setTimeout(fire, delay);
    } else {
      fire();
    }
  }

  setOpponentConnection(connected: boolean): void {
    const game = this.root.querySelector<HTMLElement>('.game');
    if (game) game.dataset.rivalLink = connected ? 'live' : 'lost';
    this.root.querySelectorAll<HTMLElement>('[data-rival-dot]').forEach((dot) => {
      dot.classList.toggle('is-live', connected);
    });
  }

  setLatency(ms: number | null): void {
    const quality = ms === null ? 'unknown' : ms < 80 ? 'good' : ms < 180 ? 'fair' : 'poor';
    this.refs.ping.textContent = ms === null ? '-- MS' : `${Math.round(ms)} MS`;
    this.refs.ping.dataset.quality = quality;
  }

  showQuestion(view: QuestionView): void {
    if (this.phase === 'staging') {
      this.cancelStaging();
      this.finishStaging();
    }
    this.phase = 'playing';
    this.setRootPhase();
    this.refs.waitingOverlay.classList.remove('is-active');
    this.refs.resultOverlay.classList.remove('is-active');
    this.refs.endOverlay.classList.remove('is-active');
    this.ending = false;
    this.currentQuestion = view.question;
    this.currentQid = view.question.id;
    this.displayAtLocal = view.displayAtLocal;
    this.deadlineLocal = view.deadlineLocal;
    this.answerEnabled = false;
    this.answered = false;
    this.selectedColor = null;
    this.selectedCell = null;
    this.clearQuestionTimers();
    this.lastQuestionGeneration += 1;
    const generation = this.lastQuestionGeneration;

    this.setPollution(view.pollution);
    this.setStabilities(view.stabilities);
    this.setProtocol(view.question.protocol, false);

    const config = PROTOCOLS[view.question.protocol];
    this.renderScreens(view.question, config);
    this.renderGrid(view.question);
    this.refs.screenWall.classList.add('is-concealed');
    this.refs.keypadLed.textContent = 'SYNCING';
    this.setDial(0);
    this.setCaption('');

    const delay = view.displayAtLocal - Date.now();
    if (delay <= 0) {
      this.revealQuestion(generation);
    } else {
      this.displayTimer = window.setTimeout(() => this.revealQuestion(generation), delay);
    }
  }

  private renderScreens(question: Question, config: (typeof PROTOCOLS)[ProtocolId]): void {
    const rng = mulberry32(hashString(question.id) ^ 0x51ed270b);
    const order = shuffle(rng, [0, 1, 2, 3]);
    this.liveScreens = order.slice(0, config.liveScreens);
    this.hopTo = config.channelHop ? order[config.liveScreens] ?? null : null;
    this.hopDone = false;
    const live = new Set(this.liveScreens);
    const targetWord = colorName(question.word, question.language);
    const targetHex = COLOR_BY_ID[question.ink].hex;
    const harsh = config.effectLevel >= 3;
    const harshHues = [356, 8, 22, 198, 284];
    const hues = [8, 24, 41, 158, 186, 212, 268, 316];
    const liveHaunt =
      config.effectLevel >= 4 ? HORROR_IMAGES[randomInt(rng, HORROR_IMAGES.length)]! : '';
    this.refs.screenWall.dataset.effect = String(config.effectLevel);
    let imageBudget = config.effectLevel >= 3 ? 1 : 0;

    for (let index = 0; index < this.refs.screens.length; index += 1) {
      const screen = this.refs.screens[index]!;
      screen.className = 'crt';
      screen.dataset.state = 'idle';
      screen.style.removeProperty('--screen-bg');
      screen.style.removeProperty('--haunt');
      const word = this.refs.screenWords[index]!;
      const image = this.refs.screenImages[index]!;
      const tag = this.refs.screenTags[index]!;
      word.textContent = '';
      word.style.color = '';
      word.dataset.ink = '';
      image.style.backgroundImage = '';
      tag.textContent = '';

      if (live.has(index)) {
        this.paintLive(screen, word, targetWord, targetHex, question, config, liveHaunt);
        continue;
      }
      this.paintDead(screen, image, tag, rng, config, harsh, hues, harshHues, () => {
        if (imageBudget > 0) {
          imageBudget -= 1;
          return true;
        }
        return false;
      });
    }
  }

  private paintLive(
    screen: HTMLElement,
    word: HTMLElement,
    targetWord: string,
    targetHex: string,
    question: Question,
    config: (typeof PROTOCOLS)[ProtocolId],
    liveHaunt: string,
  ): void {
    screen.classList.add('is-live');
    screen.dataset.state = 'live';
    if (config.effectLevel >= 1) screen.classList.add('is-flicker');
    if (config.effectLevel >= 2) screen.classList.add('is-scroll');
    if (config.effectLevel >= 3) screen.classList.add('is-distort');
    if (config.effectLevel >= 4) screen.classList.add('is-chaos');
    if (liveHaunt) screen.style.setProperty('--haunt', `url("${liveHaunt}")`);
    word.textContent = targetWord;
    word.dataset.ink = question.ink;
    word.style.color = targetHex;
    this.refs.screenTags[Number(screen.dataset.screen)]!.textContent = 'LIVE';
  }

  private paintDead(
    screen: HTMLElement,
    image: HTMLElement,
    tag: HTMLElement,
    rng: Rng,
    config: (typeof PROTOCOLS)[ProtocolId],
    harsh: boolean,
    hues: number[],
    harshHues: number[],
    takeImage: () => boolean,
  ): void {
    const hue = harsh ? harshHues[randomInt(rng, harshHues.length)]! : hues[randomInt(rng, hues.length)]!;
    const lightness = harsh ? 4 + rng() * 5 : 7 + rng() * 7;
    screen.style.setProperty('--screen-bg', `hsl(${hue} ${harsh ? 8 : 6}% ${lightness}%)`);
    const useImage = config.horror && takeImage() && rng() < 0.7;
    if (useImage) {
      screen.classList.add('is-horror');
      screen.dataset.state = 'horror';
      tag.textContent = 'GHOST';
      image.style.backgroundImage = `url("${HORROR_IMAGES[randomInt(rng, HORROR_IMAGES.length)]}")`;
      return;
    }
    screen.classList.add('is-block', 'is-flicker');
    screen.dataset.state = 'block';
    tag.textContent = 'NOISE';
  }

  /** 协议六：窗口中途目标跳到另一块屏，答案与墨色不变 */
  private hopLiveScreen(): void {
    this.hopDone = true;
    if (this.hopTo === null || !this.currentQuestion) return;
    const question = this.currentQuestion;
    const config = PROTOCOLS[question.protocol];
    const from = this.liveScreens[0] ?? 0;
    const to = this.hopTo;
    const fromScreen = this.refs.screens[from]!;
    const toScreen = this.refs.screens[to]!;
    if (!fromScreen.classList.contains('is-live') || toScreen.classList.contains('is-live')) return;

    const targetWord = colorName(question.word, question.language);
    const targetHex = COLOR_BY_ID[question.ink].hex;
    const rng = mulberry32(hashString(question.id) ^ 0x77aa11ee);
    const harsh = config.effectLevel >= 3;
    const harshHues = [356, 8, 22, 198, 284];
    const hues = [8, 24, 41, 158, 186, 212, 268, 316];
    const liveHaunt = HORROR_IMAGES[randomInt(rng, HORROR_IMAGES.length)]!;

    this.paintLive(
      toScreen,
      this.refs.screenWords[to]!,
      targetWord,
      targetHex,
      question,
      config,
      liveHaunt,
    );
    this.paintDead(
      fromScreen,
      this.refs.screenImages[from]!,
      this.refs.screenTags[from]!,
      rng,
      config,
      harsh,
      hues,
      harshHues,
      () => false,
    );
    this.liveScreens = [to];
    this.hopTo = null;
    this.hopDone = true;
    this.opts.audio.playGlitch();
    this.setCaption('CHANNEL HOP // 频道切换');
  }

  private setDial(elapsed: number): void {
    this.refs.screenWall.style.setProperty('--elapsed', String(Math.max(0, Math.min(1, elapsed))));
  }

  private resetScreens(): void {
    this.refs.screenWall.classList.add('is-concealed');
    this.refs.screenWall.dataset.effect = '0';
    this.liveScreens = [];
    this.hopTo = null;
    this.hopDone = false;
    for (let index = 0; index < this.refs.screens.length; index += 1) {
      const screen = this.refs.screens[index]!;
      screen.className = 'crt';
      screen.dataset.state = 'idle';
      this.refs.screenWords[index]!.textContent = '';
      this.refs.screenWords[index]!.style.color = '';
      this.refs.screenWords[index]!.dataset.ink = '';
      this.refs.screenImages[index]!.style.backgroundImage = '';
      this.refs.screenTags[index]!.textContent = '';
      screen.style.removeProperty('--screen-bg');
      screen.style.removeProperty('--haunt');
    }
  }

  private resetGrid(): void {
    this.refs.keypadLed.textContent = 'STANDBY';
    for (const cell of this.refs.cells) {
      cell.className = 'color-cell is-dead';
      cell.disabled = true;
      cell.dataset.color = '';
      cell.dataset.font = '';
      cell.removeAttribute('aria-label');
      cell.style.removeProperty('--key-ink');
      cell.style.removeProperty('--key-stroke');
      const label = cell.querySelector<HTMLElement>('.cell-label');
      if (label) label.textContent = '';
    }
  }

  private revealQuestion(generation: number): void {
    if (generation !== this.lastQuestionGeneration || !this.currentQuestion) return;
    const question = this.currentQuestion;
    if (this.deadlineLocal <= Date.now()) return;
    this.refs.screenWall.classList.remove('is-concealed');
    this.refs.keypadLed.textContent = 'INPUT OPEN';
    this.answerEnabled = true;
    this.opts.audio.startHorror();
    for (const cell of this.refs.cells) {
      if (cell.dataset.color) cell.disabled = false;
    }
    this.opts.audio.resume();
    if (question.voice) {
      this.opts.audio.playVoice(question.voice.clip);
      this.setCaption('VOICE FEED');
    }
    this.startTimer();
    this.deadlineTimer = window.setTimeout(() => {
      if (generation !== this.lastQuestionGeneration) return;
      this.answerEnabled = false;
      for (const cell of this.refs.cells) cell.disabled = true;
    }, Math.max(0, this.deadlineLocal - Date.now()));
  }

  private startTimer(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    const total = Math.max(1, this.deadlineLocal - this.displayAtLocal);
    this.lastDialAt = 0;
    const tick = (now: number) => {
      const remaining = this.deadlineLocal - Date.now();
      const elapsed = 1 - Math.max(0, Math.min(1, remaining / total));
      if (now - this.lastDialAt > 45) {
        this.lastDialAt = now;
        this.setDial(elapsed);
      }
      if (!this.hopDone && PROTOCOLS[this.currentQuestion?.protocol ?? 1].channelHop) {
        if (elapsed >= CHANNEL_HOP_AT) this.hopLiveScreen();
      }
      if (remaining <= 0) {
        this.setDial(1);
        this.rafId = null;
        return;
      }
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private renderGrid(question: Question): void {
    const rng = mulberry32(hashString(question.id) ^ 0x2c1b3d4e);
    const active = ACTIVE_CELLS[question.protocol];
    const shuffledWords = question.protocol === 1 ? [] : shuffle(rng, [...question.options]);
    const shuffledFonts = question.protocol === 1 ? [] : shuffle(rng, KEY_FONT_COLORS);

    this.refs.cells.forEach((cell, index) => {
      cell.className = 'color-cell';
      cell.disabled = true;
      cell.dataset.color = '';
      cell.dataset.font = '';
      cell.removeAttribute('aria-label');
      cell.style.removeProperty('--key-ink');
      cell.style.removeProperty('--key-stroke');
      const label = cell.querySelector<HTMLElement>('.cell-label')!;
      label.textContent = '';
      const activeIndex = active.indexOf(index);
      if (activeIndex < 0) {
        cell.classList.add('is-dead');
        return;
      }

      const font =
        question.protocol === 1
          ? KEY_FONT_COLORS[FIXED_CORNER_FONT_INDEX[index]!]!
          : shuffledFonts[activeIndex]!;
      const wordId =
        question.protocol === 1
          ? FIXED_CORNER_LABELS[index]!
          : shuffledWords[activeIndex]!;

      const stroke = font.light ? '1.1px rgba(0, 0, 0, 0.82)' : '0.35px rgba(0, 0, 0, 0.32)';
      cell.classList.add('is-live');
      cell.dataset.color = wordId;
      cell.dataset.font = font.hex;
      cell.style.setProperty('--key-ink', font.hex);
      cell.style.setProperty('--key-stroke', stroke);
      label.textContent = `${COLOR_BY_ID[wordId].zh}色`;
      cell.setAttribute('aria-label', `按键文字：${COLOR_BY_ID[wordId].zh}色`);
    });
  }

  showLocked(): void {
    this.answerEnabled = false;
    for (const cell of this.refs.cells) {
      cell.disabled = true;
      if (this.selectedCell !== null && cell.dataset.cell === String(this.selectedCell)) {
        cell.classList.add('is-wrong');
      }
    }
    this.triggerGlitch();
  }

  showResult(payload: RoundResultPayload): void {
    const selfWon = payload.winnerUid === this.selfUid;
    const selfCorrect = this.selectedColor === payload.correctColor;
    const tone: 'good' | 'bad' | 'neutral' =
      payload.reason === 'rejected'
        ? 'bad'
        : selfWon || (payload.reason === 'tie' && selfCorrect)
          ? 'good'
          : payload.winnerUid
            ? 'bad'
            : this.selectedColor && !selfCorrect
              ? 'bad'
              : 'neutral';
    this.answerEnabled = false;
    this.refs.keypadLed.textContent = 'LOCKED';
    for (const cell of this.refs.cells) {
      cell.disabled = true;
      if (cell.dataset.color === payload.correctColor) cell.classList.add('is-correct');
      if (this.selectedCell !== null && cell.dataset.cell === String(this.selectedCell)) {
        if (this.selectedColor !== payload.correctColor) cell.classList.add('is-wrong');
      }
    }

    const correct = COLOR_BY_ID[payload.correctColor];
    this.refs.resultCard.dataset.tone = tone;
    this.refs.resultTitle.textContent =
      payload.reason === 'rejected'
        ? '读错'
        : payload.reason === 'tie'
          ? '同步命中'
          : selfWon
            ? '确认'
            : payload.winnerUid
              ? '被抢先'
              : '窗口关闭';
    this.refs.resultColor.style.background = correct.hex;
    this.refs.resultColorName.textContent = `${correct.zh}色`;
    this.refs.resultColorName.style.color = tone === 'neutral' ? correct.hex : '';
    this.refs.resultOverlay.classList.add('is-active');
    this.setPollution(payload.pollution);
    this.setStabilities(payload.stabilities);

    if (tone === 'good') {
      this.opts.audio.playCorrect();
    } else if (tone === 'bad') {
      this.opts.audio.playWrong();
      this.triggerGlitch();
      if (this.currentProtocol >= 3 && Math.random() < 0.42) {
        this.opts.audio.playScream(0.58);
        this.triggerScare(0.58);
      }
    } else {
      this.opts.audio.playGlitch();
    }
    this.clearResultTimer();
    this.resultTimer = window.setTimeout(
      () => this.refs.resultOverlay.classList.remove('is-active'),
      760,
    );
  }

  setCaption(text: string): void {
    if (this.captionTimer !== null) {
      window.clearTimeout(this.captionTimer);
      this.captionTimer = null;
    }
    this.refs.captionText.textContent = text;
    const node = this.refs.captionText.parentElement;
    if (node) node.classList.toggle('is-live', text.length > 0);
    if (!text) return;
    this.captionTimer = window.setTimeout(() => {
      this.refs.captionText.textContent = '';
      if (node) node.classList.remove('is-live');
      this.captionTimer = null;
    }, 1900);
  }

  showSuddenDeath(): void {
    this.currentProtocol = 6;
    this.refs.projector.dataset.state = 'tag';
    this.refs.projectorTag.textContent = '加时';
    this.refs.projectorSub.textContent = '先确认者胜';
    this.refs.projector.classList.add('is-active');
    if (this.projectorTimer !== null) window.clearTimeout(this.projectorTimer);
    this.projectorTimer = window.setTimeout(() => {
      this.projectorTimer = null;
      this.refs.projector.classList.remove('is-active');
      this.opts.audio.playLampPress();
    }, 2600);
    this.opts.audio.playLampOn();
    this.opts.audio.playSuddenDeath();
    this.opts.audio.startHorror();
    this.opts.audio.setHorrorIntensity(100);
    this.opts.audio.playScream(1);
    this.triggerScare(1);
    this.setPollution(100);
    const game = this.root.querySelector<HTMLElement>('.game');
    game?.classList.add('is-sudden-death');
  }

  private maybeSpawnDrop(protocol: ProtocolId): void {
    if (this.activeDrop) this.collectActiveDrop(false);
    const chance =
      protocol === 2 ? 1 : protocol === 3 ? 0.55 : protocol === 4 ? 0.65 : protocol === 5 ? 0.75 : 0.9;
    if (Math.random() > chance) return;
    const pick = this.opts.lore.pickFragment(protocol, this.dropIds);
    if (!pick) return;
    this.dropIds.add(pick.fragment.id);
    this.activeDrop = pick;
    if (this.dropTimer !== null) {
      window.clearTimeout(this.dropTimer);
      this.dropTimer = null;
    }
    this.refs.loreDrop.hidden = false;
    this.refs.loreDrop.classList.remove('is-out');
    this.refs.loreDrop.classList.add('is-live');
    this.refs.loreDropLife.style.animation = 'none';
    void this.refs.loreDropLife.offsetWidth;
    this.refs.loreDropLife.style.animation = '';
    this.opts.audio.playGlitch();
    this.dropTimer = window.setTimeout(() => this.collectActiveDrop(false, true), 12_000);
  }

  private collectActiveDrop(expired: boolean, timedOut = false): void {
    if (this.dropTimer !== null) {
      window.clearTimeout(this.dropTimer);
      this.dropTimer = null;
    }
    const pick = this.activeDrop;
    this.activeDrop = null;
    this.refs.loreDrop.classList.remove('is-live');
    this.refs.loreDrop.classList.add('is-out');
    this.refs.loreDrop.hidden = true;
    if (!pick || expired || timedOut) return;
    const result = this.opts.lore.collect(pick.fragment.id);
    if (result) {
      this.loreResults.push(result);
      this.opts.audio.playTick(true);
    }
  }

  private awardEndLore(bonus: number): void {
    this.collectActiveDrop(true);
    const base = this.maxProtocol >= 5 ? 3 : this.maxProtocol >= 3 ? 2 : 1;
    for (let index = 0; index < base + bonus; index += 1) {
      const pick = this.opts.lore.pickFragment(this.maxProtocol, this.dropIds);
      if (!pick) break;
      this.dropIds.add(pick.fragment.id);
      const result = this.opts.lore.collect(pick.fragment.id);
      if (result) this.loreResults.push(result);
    }
    this.renderEndLore();
  }

  private renderEndStory(survived: boolean): void {
    const stage = endStory(
      this.maxProtocol,
      survived,
      this.opts.lore.getOverallProgress().docsRecovered,
    );
    this.refs.endStory.innerHTML = `<span>${esc(stage.kicker)}</span>${esc(stage.text)}`;
  }

  private renderEndLore(): void {
    if (!this.loreResults.length) {
      this.refs.endLore.innerHTML = `
        <div class="end-lore-head"><span>RECOVERED MATERIAL / 回收档案</span><b>0 件</b></div>
        <p class="end-lore-empty">本局未回收档案。提高协议等级可接触更深层的记录。</p>`;
      return;
    }
    const items = this.loreResults
      .map(
        (result) => `
        <div class="end-lore-item">
          <span class="end-lore-code">${esc(result.doc.code)}</span>
          <span class="end-lore-copy">
            <b>${esc(result.doc.title)}</b>
            <small>${esc(sectionLabel(result.doc.section))}${result.docCompleted ? ' · 卷宗完整' : ''}</small>
          </span>
          ${result.duplicate ? '<em>DUPLICATE</em>' : ''}
        </div>`,
      )
      .join('');
    this.refs.endLore.innerHTML = `
      <div class="end-lore-head">
        <span>RECOVERED MATERIAL / 回收档案</span>
        <b>${this.loreResults.length} 件</b>
      </div>
      <div class="end-lore-list">${items}</div>`;
  }

  private resetLoreRun(): void {
    if (this.dropTimer !== null) {
      window.clearTimeout(this.dropTimer);
      this.dropTimer = null;
    }
    this.activeDrop = null;
    this.dropIds.clear();
    this.loreResults = [];
    this.maxProtocol = 1;
    this.loreLeakPlayed = false;
    if (this.loreLeakTimer !== null) {
      window.clearTimeout(this.loreLeakTimer);
      this.loreLeakTimer = null;
    }
    this.refs.loreDrop.classList.remove('is-live');
    this.refs.loreDrop.hidden = true;
    this.refs.endLore.innerHTML = '';
  }

  showMatchEnd(
    payload: MatchEndPayload,
    profile: Profile | null,
    rivalName?: string,
  ): void {
    this.phase = 'ended';
    this.ending = true;
    this.opts.audio.stopHorror();
    this.setRootPhase();
    this.clearTimers();
    const selfWon = payload.winnerUid === this.selfUid;
    const draw = payload.winnerUid === null;
    const selfScore = payload.scores[this.selfUid] ?? 0;
    const rivalScore = this.rivalUid ? (payload.scores[this.rivalUid] ?? 0) : 0;
    const stats = payload.perPlayer[this.selfUid];
    const delta = payload.ratingDeltas[this.selfUid] ?? 0;
    const rating = payload.ratings[this.selfUid] ?? profile?.rating ?? 1000;
    const collapsed = payload.reason === 'collapse';
    this.refs.endStamp.textContent = selfWon ? 'ACCEPTED' : draw ? 'INCONCLUSIVE' : 'FAILED';
    this.refs.endStamp.dataset.tone = selfWon ? 'good' : draw ? 'neutral' : 'bad';
    this.refs.endTitle.textContent = collapsed
      ? selfWon
        ? '对面先垮了'
        : '灯管烧尽'
      : selfWon
        ? '校准通过'
        : draw
          ? '同步失效'
          : '校准未通过';
    this.refs.endSub.textContent = collapsed
      ? '灯管全部烧断，这一侧的人被拖出房间。'
      : payload.reason === 'forfeit'
        ? '链路中断。对局按断线处理。'
        : payload.reason === 'suddenDeath'
          ? '加时判定完成。'
          : payload.reason === 'draw'
            ? '双方都未能在加时中确认。'
            : '校准窗口结束。';
    this.refs.endScore.textContent = `${selfScore} — ${rivalScore}`;
    this.refs.endScore.classList.toggle('is-win', selfWon);
    this.refs.endRating.textContent = `${formatDelta(delta)} RATING → ${rating}`;
    this.refs.endRating.dataset.tone = delta >= 0 ? 'good' : 'bad';
    this.refs.endStats.innerHTML = `
      <div><span>协议命中</span><b>${stats?.correct ?? 0}</b></div>
      <div><span>错误输入</span><b>${stats?.wrong ?? 0}</b></div>
      <div><span>同步率</span><b>${stats?.accuracy ?? 0}%</b></div>
      <div><span>最佳反应</span><b>${reaction(stats?.bestReactionMs)}</b></div>
      <div><span>座位 / 段位</span><b>${this.seatLabel()} · ${profile ? `${profile.rating} → ${rating}` : rating}</b></div>
      <div><span>对手</span><b>${esc(rivalName ?? 'UNKNOWN')}</b></div>
    `;
    this.renderEndStory(selfWon || draw);
    this.refs.endRematchStatus.textContent = '双方同意后可再次校准。';
    this.awardEndLore(selfWon ? 1 : 0);
    this.refs.endActions.innerHTML = `
      <button class="btn btn-primary" type="button" data-action="rematch">再次校准 / REMATCH</button>
      <button class="btn btn-ghost" type="button" data-action="archive">碎片档案 / ARCHIVE</button>
      <button class="btn btn-ghost" type="button" data-action="lobby">返回大厅 / LOBBY</button>
    `;
    this.finishMatchVisual(!selfWon && !draw, selfWon, () => this.showEndOverlay());
    this.opts.audio.playMatchEnd(selfWon);
  }

  showSoloEnd(data: {
    score: number;
    correct: number;
    wrong: number;
    accuracy: number;
    bestReactionMs: number | null;
    success: boolean;
    protocol: ProtocolId;
    record: SoloRecord | null;
    isRecord: boolean;
  }): void {
    this.phase = 'ended';
    this.ending = true;
    this.opts.audio.stopHorror();
    this.setRootPhase();
    this.clearTimers();
    this.refs.endStamp.textContent = data.success ? 'TERMINATED' : 'FAILED';
    this.refs.endStamp.dataset.tone = data.success ? 'neutral' : 'bad';
    this.refs.endTitle.textContent = data.success ? '校准结束' : '认知崩溃';
    this.refs.endSub.textContent = data.success
      ? '训练窗口结束。系统已记录本次样本。'
      : '灯管全部烧断。建议休息后重新校准。';
    this.refs.endScore.textContent = String(data.score);
    this.refs.endRating.textContent = data.isRecord ? 'NEW SAMPLE RECORD' : 'SAMPLE COMPLETE';
    this.refs.endRating.dataset.tone = data.isRecord ? 'good' : 'neutral';
    this.refs.endStats.innerHTML = `
      <div><span>命中</span><b>${data.correct}</b></div>
      <div><span>错误</span><b>${data.wrong}</b></div>
      <div><span>同步率</span><b>${data.accuracy}%</b></div>
      <div><span>最佳反应</span><b>${reaction(data.bestReactionMs)}</b></div>
      <div><span>最高协议</span><b>${ROMAN[data.protocol]} / ${PROTOCOLS[data.protocol].nameZh}</b></div>
      <div><span>历史最佳</span><b>${data.record?.score ?? 0}</b></div>
    `;
    this.renderEndStory(data.success);
    this.refs.endRematchStatus.textContent = '污染数据已写入本机档案。';
    this.awardEndLore(data.success ? 1 : 0);
    this.refs.endActions.innerHTML = `
      <button class="btn btn-primary" type="button" data-action="retry">再次校准 / RETRY</button>
      <button class="btn btn-ghost" type="button" data-action="archive">碎片档案 / ARCHIVE</button>
      <button class="btn btn-ghost" type="button" data-action="lobby">返回主菜单 / MENU</button>
    `;
    this.finishMatchVisual(!data.success, false, () => this.showEndOverlay());
    this.opts.audio.playMatchEnd(data.success);
  }

  private seatLabel(): string {
    const game = this.root.querySelector<HTMLElement>('.game');
    const seat = (Number(game?.dataset.selfSeat ?? 1) || 1) as SeatId;
    return seat === 1 ? '1P / 蓝' : '2P / 红';
  }

  setRematchState(votes: string[], needed: number, selfUid: string, rivalConnected: boolean): void {
    const voted = votes.includes(selfUid);
    const count = votes.length;
    this.refs.endRematchStatus.textContent = rivalConnected
      ? voted
        ? `已同意再次校准，等待对手。(${count}/${needed})`
        : `再次校准需要双方同意。(${count}/${needed})`
      : '对手已断开，无法再次校准。';
    const button = this.refs.endActions.querySelector<HTMLButtonElement>('[data-action="rematch"]');
    if (button) {
      button.disabled = voted || !rivalConnected;
      button.textContent = voted ? '等待对手 / WAITING' : '再次校准 / REMATCH';
    }
  }

  showReconnecting(attempt: number): void {
    this.phase = 'reconnecting';
    this.setRootPhase();
    this.refs.waitingCode.textContent = this.roomCode || '------';
    this.refs.waitingOverlay.classList.add('is-active');
    this.refs.waitingHint.textContent = `链路中断，正在重建连接 (${attempt}/3)…`;
    this.answerEnabled = false;
    this.setOpponentConnection(false);
  }

  hideReconnecting(): void {
    this.refs.waitingOverlay.classList.remove('is-active');
    this.refs.waitingHint.textContent = '保持连接，匹配建立后将自动开始校准。';
  }

  showToast(message: string): void {
    const toast = this.root.querySelector<HTMLElement>('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-active');
    window.setTimeout(() => toast.classList.remove('is-active'), 2200);
  }

  private finishStaging(): void {
    this.phase = 'playing';
    this.setRootPhase();
    const game = this.root.querySelector<HTMLElement>('.game');
    game?.classList.add('is-lights-down', 'is-props-dark', 'is-keypad-ready', 'is-lamps-live');
    if (this.opts.mode === 'online') game?.classList.add('is-glass-open');
    this.refs.keypadLed.textContent = 'ARMED';
  }

  private finishMatchVisual(selfLost: boolean, rivalLost: boolean, reveal: () => void): void {
    if (this.collapseTimer !== null) {
      window.clearTimeout(this.collapseTimer);
      this.collapseTimer = null;
    }
    const game = this.root.querySelector<HTMLElement>('.game');
    this.refs.resultOverlay.classList.remove('is-active');
    if (selfLost) {
      game?.classList.add('is-collapsing');
      this.opts.audio.playShock();
      this.collapseTimer = window.setTimeout(() => {
        this.collapseTimer = null;
        reveal();
      }, 1500);
      return;
    }
    if (rivalLost) {
      game?.classList.add('is-rival-collapsing');
      this.opts.audio.playShock();
      this.collapseTimer = window.setTimeout(() => {
        this.collapseTimer = null;
        reveal();
      }, 1250);
      return;
    }
    reveal();
  }

  private resetCollapseState(): void {
    if (this.collapseTimer !== null) {
      window.clearTimeout(this.collapseTimer);
      this.collapseTimer = null;
    }
    const game = this.root.querySelector<HTMLElement>('.game');
    game?.classList.remove('is-collapsing', 'is-rival-collapsing');
  }

  private showEndOverlay(): void {
    this.refs.resultOverlay.classList.remove('is-active');
    this.refs.waitingOverlay.classList.remove('is-active');
    this.clearProjector();
    this.refs.endOverlay.classList.add('is-active');
  }

  private setRootPhase(): void {
    const game = this.root.querySelector<HTMLElement>('.game');
    if (game) game.dataset.phase = this.phase;
  }

  private triggerGlitch(): void {
    const game = this.root.querySelector<HTMLElement>('.game');
    if (!game) return;
    game.classList.add('is-glitching');
    window.setTimeout(() => game.classList.remove('is-glitching'), 380);
  }

  private triggerScare(intensity = 0.6): void {
    if (this.scareTimer !== null) window.clearTimeout(this.scareTimer);
    const image = HORROR_IMAGES[randomInt(Math.random, HORROR_IMAGES.length)];
    this.refs.scareFlash.style.backgroundImage = `url("${image}")`;
    this.refs.scareFlash.dataset.intensity = intensity >= 0.9 ? 'high' : 'low';
    this.refs.scareFlash.classList.add('is-active');
    this.scareTimer = window.setTimeout(() => {
      this.refs.scareFlash.classList.remove('is-active');
      this.scareTimer = null;
    }, intensity >= 0.9 ? 200 : 120);
  }

  private clearResultTimer(): void {
    if (this.resultTimer !== null) {
      window.clearTimeout(this.resultTimer);
      this.resultTimer = null;
    }
  }

  private clearQuestionTimers(): void {
    if (this.displayTimer !== null) {
      window.clearTimeout(this.displayTimer);
      this.displayTimer = null;
    }
    if (this.deadlineTimer !== null) {
      window.clearTimeout(this.deadlineTimer);
      this.deadlineTimer = null;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private clearCountdown(): void {
    if (this.countdownTimer !== null) {
      window.clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private clearTimers(): void {
    this.clearQuestionTimers();
    this.clearCountdown();
    this.clearResultTimer();
    this.cancelStaging();
    this.clearProjector();
    if (this.captionTimer !== null) {
      window.clearTimeout(this.captionTimer);
      this.captionTimer = null;
    }
  }

  get isEnded(): boolean {
    return this.phase === 'ended';
  }

  get currentQidValue(): string | null {
    return this.currentQid;
  }

  destroy(): void {
    this.clearTimers();
    if (this.dropTimer !== null) {
      window.clearTimeout(this.dropTimer);
      this.dropTimer = null;
    }
    if (this.loreLeakTimer !== null) {
      window.clearTimeout(this.loreLeakTimer);
      this.loreLeakTimer = null;
    }
    if (this.scareTimer !== null) window.clearTimeout(this.scareTimer);
    if (this.collapseTimer !== null) window.clearTimeout(this.collapseTimer);
    this.root.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeyDown);
    this.opts.audio.stopHorror();
    this.opts.audio.stopLoreLeak();
    this.opts.audio.setPollution(0);
    this.root.innerHTML = '';
  }
}
